import type { I18n } from '@learnforge/i18n';
import { createSvgEl } from './svg';

export interface VennSet {
  id: 'A' | 'B' | 'C';
  labelKey: string;
  cx: number;
  cy: number;
  r: number;
}

export type VennRegionId = 'A' | 'B' | 'C' | 'AB' | 'AC' | 'BC' | 'ABC' | 'none';

export interface VennConfig {
  sets: readonly VennSet[];
  universe: number;
  regionCounts: Record<string, number>;
}

export interface VennHandle {
  element: SVGSVGElement;
  update(config: VennConfig): void;
  setSelection(ids: readonly VennRegionId[]): void;
  setBusy(busy: boolean): void;
  destroy(): void;
}

export type PairRelation = 'disjoint' | 'subset' | 'superset' | 'equal' | 'overlap';

export interface CreateVennOptions {
  container: HTMLElement;
  i18n: I18n<string>;
  initial: VennConfig;
  selected?: readonly VennRegionId[];
  onSelectionChange?(selected: readonly VennRegionId[]): void;
  layout?: { mode: 'fixed' } | { mode: 'adaptive'; relation: PairRelation };
}

const SVG_W = 480;
const SVG_H = 480;
const CX = SVG_W / 2;
const CY = SVG_H / 2;

// Adaptive layout constants (deterministic — no Math.random()).
const ADAPTIVE_R_LARGE = 130;
const ADAPTIVE_R_SMALL = 70;
const ADAPTIVE_GAP = 20;

/** Compute circle positions/radii for adaptive (topologically honest) layout. */
function adaptiveGeometry(sets: readonly VennSet[], relation: PairRelation): VennSet[] {
  if (sets.length === 2) {
    const [sa, sb] = sets as [VennSet, VennSet];
    switch (relation) {
      case 'equal': {
        // Coincident circles — same centre and radius.
        const r = ADAPTIVE_R_LARGE;
        return [
          { ...sa, cx: CX, cy: CY, r },
          { ...sb, cx: CX, cy: CY, r },
        ];
      }
      case 'subset': {
        // A (small) fully inside B (large).
        const rB = ADAPTIVE_R_LARGE;
        const rA = ADAPTIVE_R_SMALL;
        // Place B at centre; offset A so it stays well inside: dist + rA < rB.
        const offset = (rB - rA) / 2;
        return [
          { ...sa, cx: CX - offset, cy: CY, r: rA },
          { ...sb, cx: CX, cy: CY, r: rB },
        ];
      }
      case 'superset': {
        // A (large) contains B (small) — mirror of subset.
        const rA = ADAPTIVE_R_LARGE;
        const rB = ADAPTIVE_R_SMALL;
        const offset = (rA - rB) / 2;
        return [
          { ...sa, cx: CX, cy: CY, r: rA },
          { ...sb, cx: CX - offset, cy: CY, r: rB },
        ];
      }
      case 'disjoint': {
        // Centres separated by rA + rB + gap.
        const r = ADAPTIVE_R_SMALL;
        const half = r + ADAPTIVE_GAP / 2;
        return [
          { ...sa, cx: CX - half, cy: CY, r },
          { ...sb, cx: CX + half, cy: CY, r },
        ];
      }
      case 'overlap':
      default: {
        // Partial overlap — centres offset by r (standard two-circle layout).
        const r = ADAPTIVE_R_LARGE;
        const half = r * 0.5;
        return [
          { ...sa, cx: CX - half, cy: CY, r },
          { ...sb, cx: CX + half, cy: CY, r },
        ];
      }
    }
  }

  // Three-set adaptive: equilateral triangle arrangement (overlap as default).
  const r = 110;
  const dist = r * 0.9;
  const angle120 = (2 * Math.PI) / 3;
  return sets.map((s, i) => ({
    ...s,
    cx: CX + dist * Math.cos(angle120 * i - Math.PI / 2),
    cy: CY + dist * Math.sin(angle120 * i - Math.PI / 2),
    r,
  }));
}

function regionCentroid(sets: readonly VennSet[], region: VennRegionId): { x: number; y: number } {
  const byId = new Map(sets.map((s) => [s.id, s]));
  const a = byId.get('A');
  const b = byId.get('B');
  const c = byId.get('C');
  switch (region) {
    case 'A':
      return b ? { x: a!.cx - a!.r * 0.55, y: a!.cy } : { x: a!.cx, y: a!.cy };
    case 'B':
      return a ? { x: b!.cx + b!.r * 0.55, y: b!.cy } : { x: b!.cx, y: b!.cy };
    case 'C':
      return { x: c!.cx, y: c!.cy + c!.r * 0.55 };
    case 'AB':
      return c
        ? { x: (a!.cx + b!.cx) / 2, y: a!.cy - a!.r * 0.2 }
        : { x: (a!.cx + b!.cx) / 2, y: a!.cy };
    case 'AC':
      return { x: (a!.cx + c!.cx) / 2 - a!.r * 0.15, y: (a!.cy + c!.cy) / 2 };
    case 'BC':
      return { x: (b!.cx + c!.cx) / 2 + b!.r * 0.15, y: (b!.cy + c!.cy) / 2 };
    case 'ABC':
      return { x: (a!.cx + b!.cx + c!.cx) / 3, y: (a!.cy + b!.cy + c!.cy) / 3 };
    case 'none':
      return { x: 36, y: SVG_H - 28 };
  }
}

function regionsForSets(sets: readonly VennSet[]): VennRegionId[] {
  if (sets.length === 3) return ['A', 'B', 'C', 'AB', 'AC', 'BC', 'ABC', 'none'];
  return ['A', 'B', 'AB', 'none'];
}

export function createVenn(opts: CreateVennOptions): VennHandle {
  let config: VennConfig = opts.initial;
  let selected = new Set<VennRegionId>(opts.selected ?? []);

  const svg = createSvgEl('svg', {
    class: 'pb-venn',
    viewBox: `0 0 ${SVG_W} ${SVG_H}`,
  });
  opts.container.appendChild(svg);

  /** Resolve the circle geometry for the current config, applying adaptive layout if requested. */
  function resolvedSets(): readonly VennSet[] {
    if (opts.layout?.mode === 'adaptive') {
      return adaptiveGeometry(config.sets, opts.layout.relation);
    }
    return config.sets;
  }

  function render(): void {
    svg.replaceChildren();
    const g = createSvgEl('g');
    svg.appendChild(g);

    const sets = resolvedSets();

    // Set circles (decorative — region clicks happen on count chips below).
    for (const set of sets) {
      const circle = createSvgEl('circle', {
        class: 'pb-venn-circle',
        'data-set': set.id,
        cx: set.cx,
        cy: set.cy,
        r: set.r,
      });
      g.appendChild(circle);
    }

    // Set labels — A/B above, C below in 3-set so it doesn't collide with the AB chip.
    for (const set of sets) {
      const below = sets.length === 3 && set.id === 'C';
      const lbl = createSvgEl('text', {
        class: 'pb-venn-label',
        x: set.cx,
        y: below ? set.cy + set.r + 20 : set.cy - set.r - 8,
        'text-anchor': 'middle',
      });
      lbl.textContent = opts.i18n.t(set.labelKey);
      g.appendChild(lbl);
    }

    // Clickable region count chips.
    const regions = regionsForSets(sets);
    for (const region of regions) {
      const count = config.regionCounts[region] ?? 0;
      const { x, y } = regionCentroid(sets, region);
      const isSelected = selected.has(region);
      const chip = createSvgEl('g', {
        class: `pb-venn-chip ${isSelected ? 'pb-venn-chip-selected' : ''}`.trim(),
        'data-region': region,
        role: 'button',
        tabindex: 0,
      });
      const bg = createSvgEl('circle', {
        class: 'pb-venn-chip-bg',
        cx: x,
        cy: y,
        r: 18,
      });
      const txt = createSvgEl('text', {
        class: 'pb-venn-chip-text',
        x,
        y,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
      });
      txt.textContent = String(count);
      chip.appendChild(bg);
      chip.appendChild(txt);
      const toggle = (): void => {
        if (selected.has(region)) selected.delete(region);
        else selected.add(region);
        render();
        opts.onSelectionChange?.([...selected]);
      };
      chip.addEventListener('click', toggle);
      chip.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          toggle();
        }
      });
      g.appendChild(chip);
    }
  }
  render();

  return {
    element: svg,
    update(next) {
      config = next;
      // Drop any selected region IDs that no longer exist (e.g. 2-set ↔ 3-set switch).
      const valid = new Set(regionsForSets(next.sets));
      selected = new Set([...selected].filter((id) => valid.has(id)));
      render();
    },
    setSelection(ids) {
      selected = new Set(ids);
      render();
    },
    setBusy(busy) {
      svg.setAttribute('aria-busy', busy ? 'true' : 'false');
    },
    destroy() {
      svg.remove();
    },
  };
}
